import React, { Component } from 'react';
import {
  Alert,
  Avatar,
  Breadcrumb,
  Button,
  Card,
  Descriptions,
  Empty,
  Modal,
  Spin,
  Table,
  Tag,
  notification,
} from 'antd';
import { Link } from 'react-router-dom';
import moment from 'moment';

import {
  getAdminUserPrizeProfile,
  getApiErrorMessage,
  getGiveawayCampaignWithFallback,
  getGiveawayParticipants,
  selectGiveawayWinner,
  formatGiveawayCampaignLoadError,
} from './api';
import WinnerInfosDisplay from './components/WinnerInfosDisplay';
import { canSelectWinnersForCampaign, getGiveawayStatusColor } from './status';
import {
  formatWinnerProgress,
  getRemainingWinnerSlots,
  getRequiredWinnerCount,
  getSelectedWinnerCount,
  getSelectedWinnerUserIds,
  isSelectionComplete,
} from './winners';

function confirmWinnerButtonLabel(selectedCount, remainingSlots, campaign) {
  const required = getRequiredWinnerCount(campaign);
  const alreadySelected = getSelectedWinnerCount(campaign);

  if (selectedCount < 1) {
    const maxPick = remainingSlots || 1;
    return maxPick === 1
      ? 'Select 1 winner'
      : `Select 1–${maxPick} winners`;
  }
  if (selectedCount === 1) {
    if (remainingSlots > 1 || alreadySelected > 0) {
      return `Confirm this winner (${alreadySelected + 1} of ${required})`;
    }
    return 'Confirm this winner';
  }
  return `Confirm ${selectedCount} winners`;
}

class Participants extends Component {
  state = {
    campaign: null,
    campaignError: '',
    loadingCampaign: true,
    loadingParticipants: true,
    participants: [],
    participantsError: '',
    pagination: {
      page: 1,
      limit: 20,
      totalCount: 0,
      totalPages: 0,
      hasNextPage: false,
      hasPrevPage: false,
    },
    selectedRowKeys: [],
    selectedParticipants: [],
    confirmModalOpen: false,
    winnerSubmitting: false,
    profileModalOpen: false,
    prizeProfile: null,
    prizeProfileError: '',
    prizeProfileLoading: false,
  };

  componentDidMount() {
    this.loadCampaignContext();
    this.loadParticipants(1);
  }

  getCampaignId() {
    return this.props.match.params.id;
  }

  loadCampaignContext = () => {
    const id = this.getCampaignId();
    this.setState({ loadingCampaign: true, campaignError: '' });
    return getGiveawayCampaignWithFallback(id)
      .then((campaign) => {
        this.setState({ campaign });
      })
      .catch((error) => {
        this.setState({
          campaignError: formatGiveawayCampaignLoadError(error),
        });
      })
      .finally(() => {
        this.setState({ loadingCampaign: false });
      });
  }

  loadParticipants = (page = 1, limit = this.state.pagination.limit) => {
    const id = this.getCampaignId();
    this.setState({ loadingParticipants: true, participantsError: '' });
    return getGiveawayParticipants(id, page, limit)
      .then((response) => {
        this.setState({
          participants: response.data || [],
          pagination: response.pagination || {
            page,
            limit,
            totalCount: 0,
            totalPages: 0,
            hasNextPage: false,
            hasPrevPage: false,
          },
        });
      })
      .catch((error) => {
        this.setState({
          participantsError: getApiErrorMessage(error, 'Failed to load campaign participants.'),
        });
      })
      .finally(() => {
        this.setState({ loadingParticipants: false });
      });
  }

  getAlreadySelectedUserIds = () => getSelectedWinnerUserIds(this.state.campaign);

  handleRowSelectionChange = (selectedRowKeys, selectedRows) => {
    const remaining = getRemainingWinnerSlots(this.state.campaign);
    const cappedKeys = selectedRowKeys.slice(0, Math.max(remaining, 0));
    const cappedRows = selectedRows.filter(
      (row) => row && row.user && cappedKeys.includes(row.user._id)
    );
    // Keep previously selected rows that may not be on this page
    const byId = {};
    this.state.selectedParticipants.forEach((p) => {
      if (p && p.user && cappedKeys.includes(p.user._id)) byId[p.user._id] = p;
    });
    cappedRows.forEach((p) => {
      if (p && p.user) byId[p.user._id] = p;
    });
    this.setState({
      selectedRowKeys: cappedKeys,
      selectedParticipants: cappedKeys.map((id) => byId[id]).filter(Boolean),
    });
  }

  openConfirmModal = () => {
    const remaining = getRemainingWinnerSlots(this.state.campaign);
    const { selectedParticipants } = this.state;
    const count = selectedParticipants.length;

    if (count < 1) {
      notification.warning({
        message: 'No participants selected',
        description: remaining > 1
          ? `Select 1 winner now, or select up to ${remaining} at once if slots are still open.`
          : 'Select 1 participant with a prize profile.',
      });
      return;
    }
    if (count > remaining) {
      notification.warning({
        message: 'Too many selected',
        description: `Only ${remaining} winner slot${remaining === 1 ? '' : 's'} remaining (${formatWinnerProgress(this.state.campaign)}).`,
      });
      return;
    }
    this.setState({ confirmModalOpen: true });
  }

  closeConfirmModal = () => {
    this.setState({ confirmModalOpen: false });
  }

  openPrizeProfileModal = (record) => {
    const userId = record.user && record.user._id;
    if (!userId) return;
    this.setState({
      profileModalOpen: true,
      prizeProfile: null,
      prizeProfileError: '',
      prizeProfileLoading: true,
    });
    getAdminUserPrizeProfile(userId)
      .then((data) => {
        this.setState({ prizeProfile: data, prizeProfileLoading: false });
      })
      .catch((error) => {
        this.setState({
          prizeProfileLoading: false,
          prizeProfileError: getApiErrorMessage(error, 'Failed to load prize profile.'),
        });
      });
  }

  closePrizeProfileModal = () => {
    this.setState({
      profileModalOpen: false,
      prizeProfile: null,
      prizeProfileError: '',
      prizeProfileLoading: false,
    });
  }

  confirmWinnerSelection = () => {
    const { campaign, selectedParticipants } = this.state;
    const userIds = selectedParticipants
      .map((p) => p && p.user && p.user._id)
      .filter(Boolean);
    if (!userIds.length) return;

    this.setState({ winnerSubmitting: true });

    // Backend: one winner → { userId }; multiple up to remaining slots → { userIds }
    const payload = userIds.length === 1
      ? { userId: userIds[0] }
      : { userIds };

    return selectGiveawayWinner(this.getCampaignId(), payload)
      .then((result) => {
        const nextCampaign = {
          ...(campaign || { _id: this.getCampaignId() }),
          status: result.status,
          winnerUserId: result.winnerUserId,
          winnerEntryId: result.winnerEntryId,
          winners: result.winners,
          winnerCount: result.winnerCount != null ? result.winnerCount : campaign && campaign.winnerCount,
          requiredWinnerCount: result.requiredWinnerCount != null
            ? result.requiredWinnerCount
            : (result.winnerCount != null ? result.winnerCount : campaign && campaign.requiredWinnerCount),
          selectionComplete: result.selectionComplete,
        };
        this.setState({
          campaign: nextCampaign,
          confirmModalOpen: false,
          selectedRowKeys: [],
          selectedParticipants: [],
        });

        const names = selectedParticipants
          .map((p) => (p.user && (p.user.username || p.user._id)) || 'Unknown')
          .join(', ');

        if (result.selectionComplete) {
          notification.success({
            message: 'Winners selected',
            description: `Selection complete. Winners: ${names}.`,
          });
        } else {
          const left = getRemainingWinnerSlots(nextCampaign);
          notification.success({
            message: 'Winner added',
            description: `${names} selected. ${formatWinnerProgress(nextCampaign)} — ${left} more slot${left === 1 ? '' : 's'} open.`,
          });
        }

        return Promise.all([
          this.loadCampaignContext(),
          this.loadParticipants(this.state.pagination.page, this.state.pagination.limit),
        ]);
      })
      .catch((error) => {
        notification.error({
          message: 'Winner selection failed',
          description: getApiErrorMessage(error, 'Unable to select winner(s) for this campaign.'),
        });
      })
      .finally(() => {
        this.setState({ winnerSubmitting: false });
      });
  }

  renderCampaignSummary() {
    const { campaign, campaignError, loadingCampaign } = this.state;
    if (loadingCampaign) {
      return (
        <Card size="small" title="Campaign Summary" style={{ marginBottom: 16 }}>
          Loading campaign details...
        </Card>
      );
    }
    if (campaignError) {
      return (
        <Alert
          type="error"
          showIcon
          style={{ marginBottom: 16 }}
          message={campaignError}
        />
      );
    }
    if (!campaign) {
      return (
        <Alert
          type="info"
          showIcon
          style={{ marginBottom: 16 }}
          message="Campaign details unavailable"
          description="Participants can still be reviewed, but the campaign summary could not be loaded."
        />
      );
    }

    const complete = isSelectionComplete(campaign);

    return (
      <Card size="small" title="Campaign Summary" style={{ marginBottom: 16 }}>
        <Descriptions bordered size="small" column={1}>
          <Descriptions.Item label="Title">{campaign.title || '-'}</Descriptions.Item>
          <Descriptions.Item label="Slug Month">{campaign.slugMonth || '-'}</Descriptions.Item>
          <Descriptions.Item label="Number of winners">
            {getRequiredWinnerCount(campaign)}
          </Descriptions.Item>
          <Descriptions.Item label="Status">
            <Tag color={getGiveawayStatusColor(campaign.status)}>{campaign.status}</Tag>
          </Descriptions.Item>
          <Descriptions.Item label="Selection Date (UTC)">
            {campaign.selectionDateUtc
              ? moment.utc(campaign.selectionDateUtc).format('YYYY-MM-DD HH:mm [UTC]')
              : '-'}
          </Descriptions.Item>
          <Descriptions.Item label="Total Entries">
            {campaign.totalEntries == null ? '-' : campaign.totalEntries}
          </Descriptions.Item>
          <Descriptions.Item label="Total Participants">
            {campaign.totalParticipants == null ? '-' : campaign.totalParticipants}
          </Descriptions.Item>
          <Descriptions.Item label="Winners">
            <WinnerInfosDisplay campaign={campaign} showProgress={false} />
          </Descriptions.Item>
          <Descriptions.Item label="Winner progress">
            <Tag color={complete ? 'blue' : 'orange'}>{formatWinnerProgress(campaign)}</Tag>
          </Descriptions.Item>
          <Descriptions.Item label="Selection complete">
            {complete ? <Tag color="blue">Yes</Tag> : <Tag>No</Tag>}
          </Descriptions.Item>
        </Descriptions>
      </Card>
    );
  }

  renderPrizeProfileModalBody() {
    const { prizeProfile, prizeProfileError, prizeProfileLoading } = this.state;
    if (prizeProfileLoading) {
      return (
        <div style={{ textAlign: 'center', padding: 24 }}>
          <Spin />
        </div>
      );
    }
    if (prizeProfileError) {
      return <Alert type="error" showIcon message={prizeProfileError} />;
    }
    if (!prizeProfile) {
      return <Empty description="No profile data." />;
    }

    const { userId, username, email, hasProfile, data } = prizeProfile;

    return (
      <>
        <Descriptions bordered size="small" column={1} style={{ marginBottom: 16 }}>
          <Descriptions.Item label="User ID">{userId || '-'}</Descriptions.Item>
          <Descriptions.Item label="Username">{username || '-'}</Descriptions.Item>
          <Descriptions.Item label="Email">{email || '-'}</Descriptions.Item>
          <Descriptions.Item label="Prize profile saved">
            {hasProfile ? <Tag color="green">Yes</Tag> : <Tag>No</Tag>}
          </Descriptions.Item>
        </Descriptions>
        {!hasProfile || !data ? (
          <Empty description="This user has not saved a shipping / prize address yet." />
        ) : (
          <Descriptions bordered size="small" column={1} title="Shipping address">
            <Descriptions.Item label="Full name">{data.fullName || '-'}</Descriptions.Item>
            <Descriptions.Item label="Phone">{data.phoneNumber || '-'}</Descriptions.Item>
            <Descriptions.Item label="Gender">{data.gender || '-'}</Descriptions.Item>
            <Descriptions.Item label="Size">{data.size || '-'}</Descriptions.Item>
            <Descriptions.Item label="Street">{data.streetAddress || '-'}</Descriptions.Item>
            <Descriptions.Item label="City">{data.city || '-'}</Descriptions.Item>
            <Descriptions.Item label="State">{data.state || '-'}</Descriptions.Item>
            <Descriptions.Item label="Postal code">{data.areaPincode || '-'}</Descriptions.Item>
            <Descriptions.Item label="Country">{data.country || '-'}</Descriptions.Item>
            <Descriptions.Item label="Confirmed at">
              {data.confirmedAt ? moment.utc(data.confirmedAt).format('YYYY-MM-DD HH:mm [UTC]') : '-'}
            </Descriptions.Item>
            <Descriptions.Item label="Profile updated at">
              {data.updatedAt ? moment.utc(data.updatedAt).format('YYYY-MM-DD HH:mm [UTC]') : '-'}
            </Descriptions.Item>
          </Descriptions>
        )}
      </>
    );
  }

  render() {
    const {
      campaign,
      confirmModalOpen,
      loadingCampaign,
      loadingParticipants,
      pagination,
      participants,
      participantsError,
      profileModalOpen,
      selectedParticipants,
      selectedRowKeys,
      winnerSubmitting,
    } = this.state;

    const winnerAllowed = canSelectWinnersForCampaign(campaign);
    const remainingSlots = getRemainingWinnerSlots(campaign);
    const alreadySelectedIds = this.getAlreadySelectedUserIds();
    const selectionComplete = isSelectionComplete(campaign);

    const rowSelection = winnerAllowed ? {
      selectedRowKeys,
      onChange: this.handleRowSelectionChange,
      hideSelectAll: remainingSlots < 2,
      getCheckboxProps: (record) => {
        const userId = record.user && record.user._id;
        const isAlreadyWinner = userId && alreadySelectedIds.includes(String(userId));
        const isSelected = selectedRowKeys.includes(userId);
        const atCap = !isSelected && selectedRowKeys.length >= remainingSlots;
        return {
          disabled: !record.hasPrizeProfile || isAlreadyWinner || atCap,
        };
      },
    } : undefined;

    const columns = [
      {
        title: 'User',
        key: 'user',
        render: (_, record) => {
          const userId = record.user && record.user._id;
          const isWinner = userId && alreadySelectedIds.includes(String(userId));
          return (
            <button
              type="button"
              className="giveaway-participants__user giveaway-participants__user--clickable"
              onClick={() => this.openPrizeProfileModal(record)}
            >
              <Avatar src={record.user && record.user.image} size={40}>
                {record.user && record.user.username ? record.user.username.charAt(0).toUpperCase() : 'U'}
              </Avatar>
              <div className="giveaway-participants__user-meta">
                <div className="giveaway-participants__user-name">
                  {record.user && record.user.username ? record.user.username : 'Unknown user'}
                  {isWinner ? <Tag color="blue" style={{ marginLeft: 8 }}>Winner</Tag> : null}
                </div>
                <div className="giveaway-participants__secondary">{userId}</div>
                <div className="giveaway-participants__hint">Click to view prize profile</div>
              </div>
            </button>
          );
        },
      },
      {
        title: 'Entries',
        dataIndex: 'entryCount',
        width: 120,
      },
      {
        title: 'Last Entry (UTC)',
        dataIndex: 'lastEntryAt',
        width: 220,
        render: (value) => (value ? moment.utc(value).format('YYYY-MM-DD HH:mm [UTC]') : '-'),
      },
      {
        title: 'Prize Profile',
        dataIndex: 'hasPrizeProfile',
        width: 140,
        render: (value) => value ? <Tag color="green">Ready</Tag> : <Tag color="red">Missing</Tag>,
      },
    ];

    return (
      <div id="giveaway-participants">
        <Breadcrumb>
          <Breadcrumb.Item><Link to="/giveaway">Giveaway</Link></Breadcrumb.Item>
          <Breadcrumb.Item>Participants</Breadcrumb.Item>
        </Breadcrumb>

        <div className="container">
          <h4>
            Campaign Participants
            <Button.Group size="small" className="is-right">
              <Button>
                <Link to={`/giveaway/${this.getCampaignId()}/edit`}>Edit Campaign</Link>
              </Button>
            </Button.Group>
          </h4>

          {selectionComplete ? (
            <Alert
              type="success"
              showIcon
              style={{ marginBottom: 16 }}
              message="Winner selection is complete"
              description="This campaign already has its winners. Selection cannot be changed from the admin UI."
            />
          ) : !winnerAllowed ? (
            <Alert
              type="warning"
              showIcon
              style={{ marginBottom: 16 }}
              message="Winner selection is locked for this campaign status"
              description={
                loadingCampaign
                  ? 'Campaign status is still loading.'
                  : 'The backend only allows winner selection when the campaign is active or closed.'
              }
            />
          ) : (
            <Alert
              type="info"
              showIcon
              style={{ marginBottom: 16 }}
              message={`Winner selection (${formatWinnerProgress(campaign || {})})`}
              description={
                remainingSlots > 1
                  ? `Select 1 winner now and confirm, or select up to ${remainingSlots} winners at once. Only participants with a prize profile can be chosen.`
                  : 'One slot remaining — select 1 participant with a prize profile and confirm.'
              }
            />
          )}

          {this.renderCampaignSummary()}

          {winnerAllowed && (
            <div className="giveaway-participants__actions">
              <Button
                type="primary"
                disabled={
                  remainingSlots === 0
                  || selectedRowKeys.length < 1
                  || selectedRowKeys.length > remainingSlots
                }
                onClick={this.openConfirmModal}
              >
                {confirmWinnerButtonLabel(selectedRowKeys.length, remainingSlots, campaign)}
              </Button>
              {selectedRowKeys.length > 0 && (
                <Button
                  onClick={() => this.setState({ selectedRowKeys: [], selectedParticipants: [] })}
                >
                  Clear selection
                </Button>
              )}
            </div>
          )}

          {participantsError && (
            <Alert
              type="error"
              showIcon
              style={{ marginBottom: 16 }}
              message={participantsError}
            />
          )}

          <Table
            rowKey={(record) => (record.user && record.user._id) || `${record.entryCount}-${record.lastEntryAt}`}
            rowSelection={rowSelection}
            columns={columns}
            loading={loadingParticipants}
            dataSource={participants}
            pagination={{
              current: pagination.page,
              pageSize: pagination.limit,
              total: pagination.totalCount,
              showSizeChanger: true,
              onChange: (page, pageSize) => this.loadParticipants(page, pageSize),
              onShowSizeChange: (page, pageSize) => this.loadParticipants(page, pageSize),
              showTotal: (total) => `Total ${total} participants`,
            }}
          />
        </div>

        <Modal
          open={profileModalOpen}
          title="Prize profile & shipping"
          footer={(
            <Button type="primary" onClick={this.closePrizeProfileModal}>
              Close
            </Button>
          )}
          onCancel={this.closePrizeProfileModal}
          width={560}
          destroyOnClose
        >
          {this.renderPrizeProfileModalBody()}
        </Modal>

        <Modal
          visible={confirmModalOpen}
          confirmLoading={winnerSubmitting}
          onCancel={this.closeConfirmModal}
          onOk={this.confirmWinnerSelection}
          okText={confirmWinnerButtonLabel(selectedParticipants.length, remainingSlots, campaign)}
          title={
            selectedParticipants.length > 1
              ? `Confirm ${selectedParticipants.length} winners`
              : 'Confirm giveaway winner'
          }
        >
          <p style={{ marginBottom: 12 }}>
            {selectedParticipants.length > 1
              ? `You are about to select ${selectedParticipants.length} winners in one step (${formatWinnerProgress(campaign || {})} so far).`
              : remainingSlots > 1
                ? `You are selecting 1 winner now (${getSelectedWinnerCount(campaign) + 1} of ${getRequiredWinnerCount(campaign)}). You can select more later.`
                : 'You are selecting the final remaining winner.'}
          </p>
          <Descriptions bordered size="small" column={1}>
            {selectedParticipants.map((p, index) => (
              <Descriptions.Item
                key={(p.user && p.user._id) || index}
                label={selectedParticipants.length > 1 ? `Winner ${index + 1}` : 'Winner'}
              >
                {(p.user && (p.user.username || p.user._id)) || 'Unknown'}
                {' · '}
                {p.entryCount} entries
                {' · '}
                {p.hasPrizeProfile ? 'Prize profile ready' : 'Missing profile'}
              </Descriptions.Item>
            ))}
          </Descriptions>
        </Modal>
      </div>
    );
  }
}

export default Participants;
